import { DEFAULT_FONT_FAMILY, OBJ, textFontCss } from '../constants';
import { ensureFontsFor, textStylesOf } from './fonts';
import { runsOf } from './richText';
import { absolutePoints } from './objects';
import { cellRect, cellStyleOf, colWidths, isCovered, mergeOf, minRowHeight } from './tableLayout';

/**
 * The single painter used for both the on-screen overlay and the exported PDF.
 *
 * Screen and export call it with different `scale` values only — that is what
 * makes the download match the preview exactly, including Bangla shaping (the
 * browser shapes the glyphs; pdf-lib's text path cannot).
 */

/* ------------------------------------------------------------------ images */

const imageCache = new Map();

/** Load (and memoise) an image source. */
export function loadImage(src) {
    if (imageCache.has(src)) return imageCache.get(src);
    const promise = new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
    imageCache.set(src, promise);
    return promise;
}

const readyImages = new Map();

/** Synchronous cache lookup for the render loop; kicks off a load on a miss. */
export function peekImage(src, onReady) {
    if (readyImages.has(src)) return readyImages.get(src);
    loadImage(src).then((img) => {
        readyImages.set(src, img);
        onReady?.();
    }).catch(() => {});
    return null;
}

/** Warm the cache so a synchronous paint (export) never misses. */
export async function preloadObjectImages(objects) {
    const sources = new Set();
    for (const obj of objects) if (obj.type === OBJ.IMAGE && obj.src) sources.add(obj.src);
    await Promise.all([...sources].map(async (src) => {
        try { readyImages.set(src, await loadImage(src)); } catch { /* skip broken image */ }
    }));
}

/* ------------------------------------------------------------------- fonts */

/**
 * Load every font the given objects are set in, plus the editor's defaults.
 *
 * Both the canvas and the exporter measure text before drawing it, so this has
 * to finish first: a family nobody has fetched yet measures as a fallback, and
 * a paragraph laid out against the wrong metrics wraps in the wrong places.
 */
export async function ensureEditorFonts(objects = []) {
    await ensureFontsFor([
        { fontFamily: DEFAULT_FONT_FAMILY },
        { fontFamily: DEFAULT_FONT_FAMILY, bold: true },
        ...textStylesOf(objects),
    ]);
    // A face can be registered and still not be the one the next `measureText`
    // sees until the loader settles.
    if (document.fonts?.ready) {
        try { await document.fonts.ready; } catch { /* ignore */ }
    }
}

/* -------------------------------------------------------------------- text */

export function textFontString(obj) {
    const weight = obj.bold ? 700 : 400;
    const style = obj.italic ? 'italic' : 'normal';
    return `${style} ${weight} ${obj.fontSize}px ${textFontCss(obj)}`;
}

const measureCtx = (() => {
    let ctx = null;
    return () => {
        if (!ctx) ctx = document.createElement('canvas').getContext('2d');
        return ctx;
    };
})();

/**
 * Word-wrap `obj.text` to the object width. Explicit newlines are kept and a
 * word longer than the box is broken by character so it can never overflow.
 */
/**
 * A substitute font is never metrically identical to the embedded one, so a
 * line that just fitted in the source can overflow by a fraction of a percent
 * and throw its last word onto the next line. Half a percent of slack keeps the
 * original line breaks and still stays inside the box padding.
 */
export const FIT_TOLERANCE = 1.005;

/**
 * Word-wrap the object into lines of styled RUNS.
 *
 * A line is `{ text, last, segments, fontSize }`: `text` is what the line reads
 * (search, the export's per-word draw), `segments` are the pieces of it that
 * share one style, and `fontSize` is the tallest of them — a line has to make
 * room for its biggest run, not for the box's default.
 *
 * `last` marks the closing line of each paragraph, which justification must
 * leave ragged exactly as a typesetter does.
 */
/**
 * Where a word may be broken with a hyphen.
 *
 * No dictionary — but not anywhere either: breaking wherever the line runs out
 * gives "documentati-on", which is worse than a ragged edge. The rule is the
 * one a typesetter uses without thinking: break between two consonants, which
 * puts the hyphen at "documen-tation" and "inter-national". At least three
 * letters stay behind and two go on.
 *
 * Latin only. Bangla is never hyphenated: its clusters do not come apart at a
 * hyphen, and breaking one spells a different word.
 */
const HYPHENATABLE = /^[A-Za-z][A-Za-z'’-]{4,}$/;
const VOWEL = /[aeiouy]/i;
function hyphenPoints(word) {
    if (!HYPHENATABLE.test(word)) return [];
    const points = [];
    for (let i = 3; i <= word.length - 2; i += 1) {
        const before = word[i - 1];
        const after = word[i];
        // …between two consonants, with a vowel somewhere behind them, so the
        // part left on the line is still pronounceable.
        if (!VOWEL.test(before) && !VOWEL.test(after) && VOWEL.test(word.slice(0, i - 1))) {
            points.push(i);
        }
    }
    // Longest first: fill the line as far as a legal break allows.
    return points.reverse();
}

export function wrapTextLines(obj) {
    const ctx = measureCtx();
    const maxWidth = Math.max(4, obj.w - TEXT_PAD * 2) * FIT_TOLERANCE;
    const widthOf = (text, style) => {
        ctx.font = textFontString(style);
        return ctx.measureText(text).width;
    };

    const out = [];
    let segments = [];
    let width = 0;

    const add = (text, style, space) => {
        const last = segments[segments.length - 1];
        if (last && last.space === space && last.style === style) last.text += text;
        else segments.push({ text, style, space });
        width += widthOf(text, style);
    };
    const flush = (last) => {
        // A wrap drops the spaces it happened on, so a line never ends in air.
        while (segments.length && segments[segments.length - 1].space) segments.pop();
        out.push({
            text: segments.map((seg) => seg.text).join(''),
            segments,
            fontSize: segments.reduce((max, seg) => Math.max(max, seg.style.fontSize || 0), 0)
                || obj.fontSize,
            last,
        });
        segments = [];
        width = 0;
    };

    for (const paragraph of runParagraphs(obj)) {
        for (const token of paragraph) {
            const tokenWidth = widthOf(token.text, token.style);
            const space = /^\s+$/.test(token.text);

            if (width + tokenWidth <= maxWidth || (!segments.length && space)) {
                add(token.text, token.style, space);
                continue;
            }
            // Hyphenation: rather than send a long word down to the next line
            // and leave a hole at the end of this one, break it and mark the
            // break. Only tried when there is a line to fill and the word is one
            // a hyphen can legally split.
            if (obj.hyphenate && segments.length && !space) {
                const room = maxWidth - width;
                let broke = false;
                for (const at of hyphenPoints(token.text)) {
                    const head = `${token.text.slice(0, at)}-`;
                    if (widthOf(head, token.style) <= room) {
                        add(head, token.style, false);
                        flush(false);
                        add(token.text.slice(at), token.style, false);
                        broke = true;
                        break;
                    }
                }
                if (broke) continue;
            }

            if (segments.length) {
                flush(false);
                if (space) continue; // the wrap swallows the space it broke on
            }
            if (tokenWidth <= maxWidth) {
                add(token.text, token.style, space);
                continue;
            }
            // A single word wider than the box — break it by character, with a
            // hyphen when hyphenation is on so the break reads as one.
            let chunk = '';
            for (const ch of token.text) {
                const mark = obj.hyphenate ? '-' : '';
                if (widthOf(chunk + ch + mark, token.style) > maxWidth && chunk) {
                    add(chunk + mark, token.style, false);
                    flush(false);
                    chunk = ch;
                } else {
                    chunk += ch;
                }
            }
            if (chunk) add(chunk, token.style, false);
        }
        flush(true);
    }

    return out.length ? out : [{ text: '', segments: [], fontSize: obj.fontSize, last: true }];
}

/** Paragraphs of `[{ text, style }]` tokens — words and the spaces between them. */
function runParagraphs(obj) {
    const paragraphs = [[]];
    for (const run of runsOf(obj)) {
        const parts = run.text.split('\n');
        parts.forEach((part, i) => {
            if (i > 0) paragraphs.push([]);
            const tokens = paragraphs[paragraphs.length - 1];
            for (const piece of part.split(/(\s+)/)) {
                if (piece) tokens.push({ text: piece, style: run.style });
            }
        });
    }
    return paragraphs;
}

/** Width of one wrapped line, its trailing spaces already gone. */
export function lineWidth(line) {
    const ctx = measureCtx();
    return line.segments.reduce((sum, seg) => {
        ctx.font = textFontString(seg.style);
        return sum + ctx.measureText(seg.text).width;
    }, 0);
}

/** Height one wrapped line occupies, driven by its tallest run. */
const lineHeightOf = (obj, line) => (line.fontSize || obj.fontSize) * (obj.lineHeight || 1.35);

export const TEXT_PAD = 3;

/** Height a text object needs for its current content. */
export function measuredTextHeight(obj) {
    const lines = wrapTextLines(obj);
    return lines.reduce((sum, line) => sum + lineHeightOf(obj, line), 0) + TEXT_PAD * 2;
}

/**
 * Width one string would occupy in a given style — used to calibrate a
 * replacement against the original run it stands in for.
 * @param {{ fontFamily: string, fontSize: number, bold?: boolean, italic?: boolean }} style
 */
export function measureLineWidth(style, text) {
    const ctx = measureCtx();
    ctx.font = textFontString(style);
    return ctx.measureText(String(text ?? '')).width;
}

/** Width of the longest wrapped line (used when a text box is first typed). */
export function measuredTextWidth(obj) {
    return Math.max(...wrapTextLines(obj).map(lineWidth), 0) + TEXT_PAD * 2;
}

/* ----------------------------------------------------------------- drawing */

function withTransform(ctx, obj, scale, paint) {
    ctx.save();
    ctx.globalAlpha = obj.opacity ?? 1;
    if (obj.rotation) {
        const cx = (obj.x + obj.w / 2) * scale;
        const cy = (obj.y + obj.h / 2) * scale;
        ctx.translate(cx, cy);
        ctx.rotate((obj.rotation * Math.PI) / 180);
        ctx.translate(-cx, -cy);
    }
    paint();
    ctx.restore();
}

/* ------------------------------------------------------------ placeholders */

/** A "[Label]" variable the user is meant to replace (the AI document drafts them). */
export const PLACEHOLDER_RE = /\[[^\[\]]{1,120}\]/g;

/** The OCR's "could not read this" markers — worth flagging even in a transcribed page. */
const UNREADABLE_RE = /^\[(অস্পষ্ট|illegible)\]$/i;

/**
 * The "[placeholder]" tokens in `text`. A paragraph read off an uploaded page
 * (`plainBrackets`) has brackets that are just its own text — "[40 Marks]" on an
 * exam — so there only the unreadable-word markers count as fields.
 */
export function placeholderMatches(text, plainBrackets = false) {
    const all = [...String(text || '').matchAll(PLACEHOLDER_RE)];
    return plainBrackets ? all.filter((m) => UNREADABLE_RE.test(m[0])) : all;
}

/**
 * Per-line [start, end) character ranges of "[placeholder]" tokens. Lines are joined with the
 * one space a wrap swallows, so a token that wrapped onto a second line is still found whole.
 */
function placeholderRangesByLine(lines, plainBrackets = false) {
    const joined = lines.map((l) => l.text).join(' ');
    const starts = [];
    let at = 0;
    for (const l of lines) { starts.push(at); at += l.text.length + 1; }
    const out = lines.map(() => []);
    for (const m of placeholderMatches(joined, plainBrackets)) {
        const a = m.index;
        const b = a + m[0].length;
        lines.forEach((l, i) => {
            const from = Math.max(a, starts[i]);
            const to = Math.min(b, starts[i] + l.text.length);
            if (to > from) out[i].push([from - starts[i], to - starts[i]]);
        });
    }
    return out;
}

/** Cut a line's styled segments at the placeholder edges, tagging the pieces inside one. */
function splitAtPlaceholders(line, ranges) {
    if (!ranges.length) return line.segments;
    const out = [];
    let pos = 0;
    for (const seg of line.segments) {
        const segStart = pos;
        const segEnd = pos + seg.text.length;
        pos = segEnd;
        const cuts = new Set([segStart, segEnd]);
        for (const [a, b] of ranges) {
            if (a > segStart && a < segEnd) cuts.add(a);
            if (b > segStart && b < segEnd) cuts.add(b);
        }
        const pts = [...cuts].sort((x, y) => x - y);
        for (let i = 0; i < pts.length - 1; i += 1) {
            const from = pts[i];
            const to = pts[i + 1];
            if (to <= from) continue;
            out.push({
                ...seg,
                text: seg.text.slice(from - segStart, to - segStart),
                ph: ranges.some(([a, b]) => from >= a && to <= b),
            });
        }
    }
    return out;
}

function drawTextObject(ctx, obj, scale, opts = {}) {
    const lines = wrapTextLines(obj);

    // The export draws the patch as a real PDF rectangle instead (see
    // `stampLayer`): baked into the stamp it ends at the image's alpha edge,
    // which viewers that interpolate images (Chrome's) render as a faint grey
    // rectangle around the paragraph.
    if (obj.background && obj.background !== 'transparent' && !opts.vectorPatchIds?.has(obj.id)) {
        ctx.fillStyle = obj.background;
        ctx.fillRect(obj.x * scale, obj.y * scale, obj.w * scale, obj.h * scale);
    }

    const totalHeight = lines.reduce((sum, line) => sum + lineHeightOf(obj, line), 0);
    ctx.save();
    ctx.beginPath();
    ctx.rect(obj.x * scale, obj.y * scale, obj.w * scale, Math.max(obj.h, totalHeight) * scale);
    ctx.clip();
    ctx.textBaseline = 'alphabetic';

    const inner = (obj.w - TEXT_PAD * 2) * scale;
    const left = (obj.x + TEXT_PAD) * scale;

    // Display only (never the export): show "[placeholders]" as the yellow chips the old editor had.
    const markPh = !!opts.markPlaceholders && !opts.forExport;
    const phByLine = markPh ? placeholderRangesByLine(lines, !!obj.plainBrackets) : null;

    let top = (obj.y + TEXT_PAD) * scale;
    for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
        const line = lines[lineIndex];
        const lineH = lineHeightOf(obj, line) * scale;
        const baseline = top + (line.fontSize || obj.fontSize) * 0.82 * scale;
        top += lineH;
        if (!line.segments.length) continue;

        const measured = lineWidth(line) * scale;
        // Justify by spreading the slack across the gaps, which is what makes a
        // replaced paragraph sit flush with an untouched one beside it.
        const gaps = obj.align === 'justify' && !line.last
            ? line.segments.filter((seg) => seg.space).length
            : 0;
        const slack = gaps && measured < inner ? (inner - measured) / gaps : 0;

        let pen = left;
        if (!slack) {
            if (obj.align === 'center') pen += (inner - measured) / 2;
            else if (obj.align === 'right') pen += inner - measured;
        }

        for (const seg of (markPh ? splitAtPlaceholders(line, phByLine[lineIndex]) : line.segments)) {
            const style = seg.style;
            ctx.font = `${style.italic ? 'italic' : 'normal'} ${style.bold ? 700 : 400} ${style.fontSize * scale}px ${textFontCss(style)}`;
            const width = ctx.measureText(seg.text).width;
            if (seg.ph) {
                ctx.fillStyle = '#fff3cd';
                const padX = 1.5 * scale;
                const chipY = baseline - style.fontSize * 0.98 * scale;
                const chipH = style.fontSize * 1.32 * scale;
                ctx.beginPath();
                if (ctx.roundRect) ctx.roundRect(pen - padX, chipY, width + padX * 2, chipH, 3 * scale);
                else ctx.rect(pen - padX, chipY, width + padX * 2, chipH);
                ctx.fill();
            }
            ctx.fillStyle = style.color;
            if (!seg.space) {
                if (style.shadow) {
                    ctx.save();
                    ctx.shadowColor = 'rgba(28,24,21,0.45)';
                    ctx.shadowBlur = Math.max(1, style.fontSize * 0.14 * scale);
                    ctx.shadowOffsetX = style.fontSize * 0.06 * scale;
                    ctx.shadowOffsetY = style.fontSize * 0.08 * scale;
                    ctx.fillText(seg.text, pen, baseline);
                    ctx.restore();
                }
                ctx.fillText(seg.text, pen, baseline);
                if (style.outline) {
                    ctx.save();
                    ctx.lineWidth = Math.max(0.6, style.fontSize * 0.045 * scale);
                    ctx.strokeStyle = '#1C1815';
                    ctx.strokeText(seg.text, pen, baseline);
                    ctx.restore();
                }
                // Underlining per run is the point of styling a selection.
                if (style.underline) {
                    ctx.fillRect(
                        pen,
                        baseline + style.fontSize * 0.16 * scale,
                        width,
                        Math.max(1, style.fontSize * 0.06 * scale),
                    );
                }
                // Struck through: the rule sits on the middle of the lower-case
                // letters, which is roughly a third of the size above the baseline.
                if (style.strike) {
                    ctx.fillRect(
                        pen,
                        baseline - style.fontSize * 0.3 * scale,
                        width,
                        Math.max(1, style.fontSize * 0.06 * scale),
                    );
                }
            }
            pen += width + (seg.space ? slack : 0);
        }
    }

    ctx.restore();
}

function drawInk(ctx, obj, scale) {
    const points = absolutePoints(obj);
    if (points.length < 2) {
        const [p] = points;
        if (!p) return;
        ctx.fillStyle = obj.color;
        ctx.beginPath();
        ctx.arc(p[0] * scale, p[1] * scale, (obj.strokeWidth / 2) * scale, 0, Math.PI * 2);
        ctx.fill();
        return;
    }

    ctx.strokeStyle = obj.color;
    ctx.lineWidth = obj.strokeWidth * scale;
    ctx.lineCap = obj.mode === 'highlight' ? 'butt' : 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(points[0][0] * scale, points[0][1] * scale);
    // Quadratic midpoints smooth the pointer samples without a curve-fitting pass.
    for (let i = 1; i < points.length - 1; i += 1) {
        const [x1, y1] = points[i];
        const [x2, y2] = points[i + 1];
        ctx.quadraticCurveTo(x1 * scale, y1 * scale, ((x1 + x2) / 2) * scale, ((y1 + y2) / 2) * scale);
    }
    const last = points[points.length - 1];
    ctx.lineTo(last[0] * scale, last[1] * scale);
    ctx.stroke();
}

function strokeAndFillRect(ctx, obj, scale, path) {
    if (obj.fill && obj.fill !== 'transparent') {
        ctx.fillStyle = obj.fill;
        path();
        ctx.fill();
    }
    if (obj.strokeWidth > 0 && obj.stroke && obj.stroke !== 'transparent') {
        ctx.strokeStyle = obj.stroke;
        ctx.lineWidth = obj.strokeWidth * scale;
        path();
        ctx.stroke();
    }
}

function lineEnds(obj, scale) {
    const x1 = (obj.flipX ? obj.x + obj.w : obj.x) * scale;
    const y1 = (obj.flipY ? obj.y + obj.h : obj.y) * scale;
    const x2 = (obj.flipX ? obj.x : obj.x + obj.w) * scale;
    const y2 = (obj.flipY ? obj.y : obj.y + obj.h) * scale;
    return [x1, y1, x2, y2];
}

function drawArrowHead(ctx, x1, y1, x2, y2, size) {
    const angle = Math.atan2(y2 - y1, x2 - x1);
    ctx.beginPath();
    ctx.moveTo(x2, y2);
    ctx.lineTo(x2 - size * Math.cos(angle - Math.PI / 7), y2 - size * Math.sin(angle - Math.PI / 7));
    ctx.lineTo(x2 - size * Math.cos(angle + Math.PI / 7), y2 - size * Math.sin(angle + Math.PI / 7));
    ctx.closePath();
    ctx.fill();
}

function drawNote(ctx, obj, scale) {
    const x = obj.x * scale;
    const y = obj.y * scale;
    const w = obj.w * scale;
    const h = obj.h * scale;
    const fold = Math.min(w, h) * 0.28;

    ctx.fillStyle = obj.color;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + w - fold, y);
    ctx.lineTo(x + w, y + fold);
    ctx.lineTo(x + w, y + h);
    ctx.lineTo(x, y + h);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = 'rgba(0,0,0,0.18)';
    ctx.beginPath();
    ctx.moveTo(x + w - fold, y);
    ctx.lineTo(x + w, y + fold);
    ctx.lineTo(x + w - fold, y + fold);
    ctx.closePath();
    ctx.fill();

    const fontSize = Math.max(7, Math.min(11, obj.h / 6));
    const textObj = {
        ...obj,
        x: obj.x + 4,
        y: obj.y + 5,
        w: obj.w - 8,
        h: obj.h - 10,
        text: obj.text,
        fontFamily: DEFAULT_FONT_FAMILY,
        fontStack: null,
        fontSize,
        color: '#1C1815',
        align: 'left',
        lineHeight: 1.25,
        background: 'transparent',
        rotation: 0,
    };
    drawTextObject(ctx, textObj, scale);
}

function drawCheck(ctx, obj, scale) {
    const x = obj.x * scale;
    const y = obj.y * scale;
    const w = obj.w * scale;
    const h = obj.h * scale;
    ctx.strokeStyle = obj.color;
    ctx.lineWidth = Math.max(1, obj.strokeWidth * scale);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(x + w * 0.12, y + h * 0.55);
    ctx.lineTo(x + w * 0.4, y + h * 0.82);
    ctx.lineTo(x + w * 0.9, y + h * 0.16);
    ctx.stroke();
}

function drawCross(ctx, obj, scale) {
    const x = obj.x * scale;
    const y = obj.y * scale;
    const w = obj.w * scale;
    const h = obj.h * scale;
    ctx.strokeStyle = obj.color;
    ctx.lineWidth = Math.max(1, obj.strokeWidth * scale);
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(x + w * 0.14, y + h * 0.14);
    ctx.lineTo(x + w * 0.86, y + h * 0.86);
    ctx.moveTo(x + w * 0.86, y + h * 0.14);
    ctx.lineTo(x + w * 0.14, y + h * 0.86);
    ctx.stroke();
}

/**
 * Paint one object.
 * @param {CanvasRenderingContext2D} ctx
 * @param {object} obj
 * @param {number} scale pixels per PDF point
 * @param {{ onImageReady?: () => void, forExport?: boolean,
 *   vectorPatchIds?: Set<string> }} [opts]
 */
/** The text-style a cell is set in: the table's shared style, then the cell's own overrides. */
function cellStyleFor(t, r, c) {
    const own = cellStyleOf(t, r, c);
    return {
        fontSize: own.fontSize ?? t.fontSize,
        bold: !!own.bold,
        italic: !!own.italic,
        align: own.align ?? t.align,
        valign: own.valign ?? t.valign ?? 'top',
    };
}

/**
 * A pseudo TEXT object for one table cell, used to measure, draw and edit its text.
 * `rect` is the cell's page-space rect (the whole merged area for a merged cell); a cell set
 * to vertical-middle has its text centred in it.
 */
export function cellTextObject(t, r, c, rect = cellRect(t, r, c), textOverride = null) {
    const padX = t.padX ?? 6;
    const padY = t.padY ?? 4;
    const style = cellStyleFor(t, r, c);
    const text = textOverride ?? (t.cells?.[r]?.[c] || '');
    const obj = {
        type: OBJ.TEXT,
        x: rect.x + padX,
        y: rect.y + padY,
        w: Math.max(4, rect.w - padX * 2),
        h: Math.max(4, rect.h - padY * 2),
        rotation: 0,
        opacity: 1,
        text,
        fontSize: style.fontSize,
        lineHeight: t.lineHeight,
        fontStack: t.fontStack,
        fontFamily: t.fontFamily,
        color: t.color,
        align: style.align,
        bold: style.bold,
        italic: style.italic,
        underline: false,
        background: 'transparent',
        marks: null,
    };
    if (style.valign === 'middle' && text) {
        const textH = measuredTextHeight(obj) - TEXT_PAD * 2;
        obj.y = rect.y + Math.max(padY, (rect.h - textH) / 2);
    }
    return obj;
}

/**
 * Recompute a table's row heights from its cell text (each row grows to its tallest
 * cell, never below the one-line minimum or the row's own `rowMin`) and set
 * `h = sum(rowHeights)`. Merged cells measure against their whole width; a cell that
 * spans rows asks the last row it covers for whatever the others don't already give it. Pure.
 */
export function recomputeTable(t) {
    const widths = colWidths(t);
    const padX = t.padX ?? 6;
    const padY = t.padY ?? 4;
    const floor = minRowHeight(t);
    const need = Array.from({ length: t.rows }, () => floor);
    const tall = [];
    for (let r = 0; r < t.rows; r += 1) {
        for (let c = 0; c < t.cols; c += 1) {
            if (isCovered(t, r, c)) continue;
            const text = t.cells?.[r]?.[c];
            if (!text) continue;
            const m = mergeOf(t, r, c);
            const cs = m ? m.cs : 1;
            const rs = m ? m.rs : 1;
            let w = 0;
            for (let i = c; i < c + cs; i += 1) w += widths[i] || 0;
            const style = cellStyleFor(t, r, c);
            const pseudo = {
                type: OBJ.TEXT, text, w: Math.max(4, w - padX * 2), fontSize: style.fontSize, bold: style.bold,
                italic: style.italic, lineHeight: t.lineHeight, fontStack: t.fontStack, fontFamily: t.fontFamily, marks: null,
            };
            const th = measuredTextHeight(pseudo) - TEXT_PAD * 2 + padY * 2;
            if (rs === 1) need[r] = Math.max(need[r], th);
            else tall.push({ r, rs, th });
        }
    }
    const rowHeights = need.map((h, r) => Math.max(h, t.rowMin?.[r] || 0));
    for (const { r, rs, th } of tall) {
        let have = 0;
        for (let i = r; i < r + rs; i += 1) have += rowHeights[i] || 0;
        if (have < th) rowHeights[r + rs - 1] += th - have;
    }
    return { ...t, rowHeights, h: rowHeights.reduce((sum, v) => sum + v, 0) };
}

/** Paint a table: each cell's rules (a merged area is one cell), then the text of each non-empty cell. */
function drawTableObject(ctx, t, scale, opts) {
    ctx.save();
    ctx.strokeStyle = t.borderColor || '#1C1815';
    const ruled = t.borderWidth !== 0;
    ctx.lineWidth = Math.max(0.4, (t.borderWidth || 1) * scale);
    for (let r = 0; r < t.rows; r += 1) {
        for (let c = 0; c < t.cols; c += 1) {
            if (isCovered(t, r, c)) continue;
            const rect = cellRect(t, r, c);
            if (ruled) ctx.strokeRect(rect.x * scale, rect.y * scale, rect.w * scale, rect.h * scale);
        }
    }
    ctx.restore();
    // Cell text.
    for (let r = 0; r < t.rows; r += 1) {
        for (let c = 0; c < t.cols; c += 1) {
            if (isCovered(t, r, c) || !t.cells?.[r]?.[c]) continue;
            // The cell being typed into is drawn by the live editor.
            if (opts.skipCell && opts.skipCell.id === t.id && opts.skipCell.r === r && opts.skipCell.c === c) continue;
            drawTextObject(ctx, cellTextObject(t, r, c), scale, opts);
        }
    }
}

export function drawObject(ctx, obj, scale, opts = {}) {
    withTransform(ctx, obj, scale, () => {
        switch (obj.type) {
            case OBJ.TEXT:
                drawTextObject(ctx, obj, scale, opts);
                break;

            case OBJ.TABLE:
                drawTableObject(ctx, obj, scale, opts);
                break;

            case OBJ.WHITEOUT:
                // Drawn as a real rectangle by the exporter — see `vectorPatchIds`.
                if (opts.vectorPatchIds?.has(obj.id)) break;
                ctx.fillStyle = obj.fill;
                ctx.fillRect(obj.x * scale, obj.y * scale, obj.w * scale, obj.h * scale);
                break;

            case OBJ.INK:
                drawInk(ctx, obj, scale);
                break;

            case OBJ.RECT:
                strokeAndFillRect(ctx, obj, scale, () => {
                    ctx.beginPath();
                    ctx.rect(obj.x * scale, obj.y * scale, obj.w * scale, obj.h * scale);
                });
                break;

            case OBJ.ELLIPSE:
                strokeAndFillRect(ctx, obj, scale, () => {
                    ctx.beginPath();
                    ctx.ellipse(
                        (obj.x + obj.w / 2) * scale,
                        (obj.y + obj.h / 2) * scale,
                        Math.max(0.5, (obj.w / 2) * scale),
                        Math.max(0.5, (obj.h / 2) * scale),
                        0, 0, Math.PI * 2,
                    );
                });
                break;

            case OBJ.LINE:
            case OBJ.ARROW: {
                const [x1, y1, x2, y2] = lineEnds(obj, scale);
                ctx.strokeStyle = obj.stroke;
                ctx.lineWidth = Math.max(0.5, obj.strokeWidth * scale);
                ctx.lineCap = 'round';
                ctx.beginPath();
                ctx.moveTo(x1, y1);
                ctx.lineTo(x2, y2);
                ctx.stroke();
                if (obj.type === OBJ.ARROW) {
                    ctx.fillStyle = obj.stroke;
                    drawArrowHead(ctx, x1, y1, x2, y2, Math.max(6, obj.strokeWidth * 3.5) * scale);
                }
                break;
            }

            case OBJ.IMAGE: {
                const img = peekImage(obj.src, opts.onImageReady);
                if (img) {
                    ctx.drawImage(img, obj.x * scale, obj.y * scale, obj.w * scale, obj.h * scale);
                } else if (!opts.forExport) {
                    ctx.fillStyle = 'rgba(0,0,0,0.06)';
                    ctx.fillRect(obj.x * scale, obj.y * scale, obj.w * scale, obj.h * scale);
                }
                break;
            }

            case OBJ.CHECK:
                drawCheck(ctx, obj, scale);
                break;

            case OBJ.CROSS:
                drawCross(ctx, obj, scale);
                break;

            case OBJ.NOTE:
                drawNote(ctx, obj, scale);
                break;

            case OBJ.LINK: {
                // The clickable rect is a real PDF annotation. On the page it
                // gets a tint and a dashed edge so an area link is visible
                // while editing; only the underline is stamped into the export.
                if (!opts.forExport) {
                    ctx.save();
                    ctx.globalAlpha = 0.14;
                    ctx.fillStyle = obj.color;
                    ctx.fillRect(obj.x * scale, obj.y * scale, obj.w * scale, obj.h * scale);
                    ctx.globalAlpha = 0.7;
                    ctx.strokeStyle = obj.color;
                    ctx.lineWidth = Math.max(1, scale * 0.75);
                    ctx.setLineDash([4 * scale, 3 * scale]);
                    ctx.strokeRect(obj.x * scale, obj.y * scale, obj.w * scale, obj.h * scale);
                    ctx.restore();
                }
                ctx.fillStyle = obj.color;
                ctx.globalAlpha = (obj.opacity ?? 1) * 0.9;
                ctx.fillRect(
                    obj.x * scale,
                    (obj.y + obj.h) * scale - Math.max(1, scale),
                    obj.w * scale,
                    Math.max(1, scale),
                );
                break;
            }

            default:
                break;
        }
    });
}

const isHighlightObject = (obj) => obj.type === OBJ.INK && obj.mode === 'highlight';

/**
 * Paint a page's objects onto `ctx`.
 * @param {'normal'|'highlight'|'all'} layer highlights are painted separately so
 *   the export can stamp them with a PDF multiply blend and keep text readable.
 */
export function paintObjects(ctx, objects, scale, { layer = 'all', skipIds, ...opts } = {}) {
    for (const obj of objects) {
        if (skipIds?.has?.(obj.id)) continue;
        if (layer === 'highlight' && !isHighlightObject(obj)) continue;
        if (layer === 'normal' && isHighlightObject(obj)) continue;
        drawObject(ctx, obj, scale, opts);
    }
}

export const hasHighlights = (objects) => objects.some(isHighlightObject);
export const hasNonHighlights = (objects) => objects.some((o) => !isHighlightObject(o));

/** Union bounding box of objects, padded, clamped to the page — in points. */
export function objectsBounds(objects, pageW, pageH, pad = 6) {
    let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
    for (const obj of objects) {
        // A rotated box can stick out past its axis-aligned rect; use the
        // circumscribed square so nothing is clipped from the stamp.
        const cx = obj.x + obj.w / 2;
        const cy = obj.y + obj.h / 2;
        const reach = obj.rotation ? Math.hypot(obj.w, obj.h) / 2 : 0;
        const x0 = obj.rotation ? cx - reach : obj.x;
        const y0 = obj.rotation ? cy - reach : obj.y;
        const x1 = obj.rotation ? cx + reach : obj.x + obj.w;
        const y1 = obj.rotation ? cy + reach : obj.y + obj.h;
        if (x0 < minX) minX = x0;
        if (y0 < minY) minY = y0;
        if (x1 > maxX) maxX = x1;
        if (y1 > maxY) maxY = y1;
    }
    if (!Number.isFinite(minX)) return null;
    return {
        x: Math.max(0, minX - pad),
        y: Math.max(0, minY - pad),
        w: Math.min(pageW, maxX + pad) - Math.max(0, minX - pad),
        h: Math.min(pageH, maxY + pad) - Math.max(0, minY - pad),
    };
}
